export type ServerSentEventMessage = {
    event: string;
    data: string;
};

type ReadServerSentEventsOptions = {
    url: string;
    signal: AbortSignal;
    headers?: HeadersInit;
    credentials?: RequestCredentials;
    onMessage: (message: ServerSentEventMessage) => void;
    onConnected?: () => void | Promise<void>;
    heartbeatTimeoutMs?: number;
    isHeartbeat?: (message: ServerSentEventMessage) => boolean;
};

const EVENT_DELIMITER = /\r?\n\r?\n/;
const DEFAULT_HEARTBEAT_TIMEOUT_MS = 16000;

const parseServerSentEventBlock = (eventBlock: string): ServerSentEventMessage => {
    const lines = eventBlock.split(/\r?\n/);
    const dataLines: string[] = [];
    let event = 'message';

    lines.forEach((line) => {
        if (!line || line.startsWith(':')) return;

        const [field, ...rest] = line.split(':');
        const value = rest.join(':').replace(/^\s/, '');

        if (field === 'event') {
            event = value || 'message';
        } else if (field === 'data') {
            dataLines.push(value);
        }
    });

    return {
        event,
        data: dataLines.join('\n'),
    };
};

const drainServerSentEventsBuffer = (buffer: string, onMessage: (message: ServerSentEventMessage) => void) => {
    const eventBlocks = buffer.split(EVENT_DELIMITER);
    const pendingBuffer = eventBlocks.pop() || '';

    eventBlocks.forEach((eventBlock) => {
        if (!eventBlock.trim()) return;
        onMessage(parseServerSentEventBlock(eventBlock));
    });

    return pendingBuffer;
};

export const readServerSentEvents = async ({
    url,
    signal,
    headers,
    credentials,
    onMessage,
    onConnected,
    heartbeatTimeoutMs = DEFAULT_HEARTBEAT_TIMEOUT_MS,
    isHeartbeat = (message) => message.event === 'ping',
}: ReadServerSentEventsOptions) => {
    const response = await fetch(url, {
        credentials,
        headers: {
            Accept: 'text/event-stream',
            ...headers,
        },
        signal,
    });

    if (!response.ok) {
        throw new Error(`Stream request failed with status ${response.status}`);
    }

    if (!response.body) {
        throw new Error('Stream response missing body');
    }

    if (onConnected) await onConnected();

    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';
    let heartbeatTimeout: ReturnType<typeof setTimeout> | null = null;
    let lastPingAt = Date.now();
    let heartbeatTimedOut = false;

    const scheduleHeartbeatCheck = () => {
        if (heartbeatTimeoutMs <= 0) return;

        if (heartbeatTimeout) clearTimeout(heartbeatTimeout);

        heartbeatTimeout = setTimeout(() => {
            if (Date.now() - lastPingAt <= heartbeatTimeoutMs) {
                scheduleHeartbeatCheck();
                return;
            }

            heartbeatTimedOut = true;
            reader.cancel().catch(() => undefined);
        }, heartbeatTimeoutMs);
    };

    // A successful response is connected; every server ping keeps that connection alive.
    scheduleHeartbeatCheck();

    const handleMessage = (message: ServerSentEventMessage) => {
        if (isHeartbeat(message)) {
            lastPingAt = Date.now();
            scheduleHeartbeatCheck();
        }

        onMessage(message);
    };

    try {
        while (!signal.aborted) {
            // Decode and drain complete SSE events while keeping partial chunks buffered for the next read.
            // eslint-disable-next-line no-await-in-loop
            const { done, value } = await reader.read();
            if (done) break;
            if (value) {
                buffer += decoder.decode(value, { stream: true });
                buffer = drainServerSentEventsBuffer(buffer, handleMessage);
            }
        }

        // Flush any remaining decoded bytes/events when the stream closes.
        buffer += decoder.decode();
        buffer = drainServerSentEventsBuffer(buffer, handleMessage);
        if (buffer.trim()) {
            handleMessage(parseServerSentEventBlock(buffer));
            buffer = '';
        }

        if (heartbeatTimedOut) {
            throw new Error(`Stream heartbeat timed out after ${heartbeatTimeoutMs}ms`);
        }
    } finally {
        if (heartbeatTimeout) clearTimeout(heartbeatTimeout);
        reader.releaseLock();
    }
};
